import { BaseApiClient, IApiInterface } from 'fitbeat.api-clients';
import { IMemberDetailsData } from 'fitbeat.gym-core';
import {
  IAccountComment,
  ICreateSpecialOfferParams,
  IOrganisationConfig,
  IPagedQueryResult,
  ISpecialOfferDisplay,
  IUserWorkoutSessionMemberReport,
  LeadStatus,
  MembersReportData,
  UserAccountDetail,
  UserProfile,
} from 'fitbeat.models';
import fetch from 'isomorphic-fetch';

export const WORKOUT_PAGE_SIZE = 2000;

export interface IMonthlyPulsePeriod {
  /** YYYY-MM for the legacy single-month path. */
  month?: string;
  /** Arbitrary from/to range (weekly or custom periods). */
  from?: Date;
  to?: Date;
}

/**
 * Member-eligibility options for a pulse run: sent on the support endpoint
 * (server filters badges/leaderboards) and applied client-side to the member
 * stats. Both default to false (exclude test users and un-onboarded members).
 */
export interface IPulseRunOptions {
  includeTestUsers: boolean;
  includeUnonboarded: boolean;
}

export class MembersService extends BaseApiClient {
  private logger: any;

  public constructor(config: IApiInterface) {
    super(config, fetch, undefined);
    this.logger = (() => {
      // empty
    });
    this.fetch = fetch;
  }

  public async getOrginisationConfig(gymName: string): Promise<IOrganisationConfig> {
    this.logger('[BusinessToolsClient] getting org gym');
    const request = await this.buildGetRequest(`business-tools/gyms/${gymName}/organisation`, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getMembers(
    { emailExclusions, gymName }
    : { emailExclusions?: string; gymName: string; },
    workoutsDateRange?: { workoutsFromDate: Date, workoutsToDate?: Date },
    onProgress?: (loaded: number) => void,
    monthlyPulse?: IMonthlyPulsePeriod,
  ): Promise<IMemberDetailsData[]> {
    this.logger('[BusinessToolsClient] getting members for gym');
    const pageLimit = 500;

    const membersByUid = new Map<string, IMemberDetailsData>();
    let lastPageToken: string | null = null;
    do {
      const params = new URLSearchParams({ limit: String(pageLimit) });
      if (emailExclusions) {
        params.set('emailExclusions', emailExclusions);
      }
      if (workoutsDateRange?.workoutsFromDate) {
        params.set('workoutsFromDate', workoutsDateRange.workoutsFromDate.toISOString());
      }
      if (workoutsDateRange?.workoutsToDate) {
        params.set('workoutsToDate', workoutsDateRange.workoutsToDate.toISOString());
      }
      if (lastPageToken) {
        params.set('lastPageToken', lastPageToken);
      }
      if (monthlyPulse?.month) {
        params.set('monthlyPulseMonth', monthlyPulse.month);
      }
      if (monthlyPulse?.from && monthlyPulse?.to) {
        params.set('monthlyPulseFrom', monthlyPulse.from.toISOString());
        params.set('monthlyPulseTo', monthlyPulse.to.toISOString());
      }

      const url = `business-tools/gyms/${gymName}/members?${params.toString()}`;
      const request = await this.buildGetRequest(url, true);
      const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
      const page: IPagedQueryResult<IMemberDetailsData> = await this.assertHttp200AndGetJsonFromResult(result);
      for (const m of page.items) {
        membersByUid.set(m.userSettings.uid, m);
      }
      onProgress?.(membersByUid.size);
      lastPageToken = page.lastPageToken;
    } while (lastPageToken !== null);

    return Array.from(membersByUid.values());
  }

  public async getMemberDetails(uid: string): Promise<UserAccountDetail> {
    this.logger(`[BusinessToolsClient] getting member detail for ${uid}`);
    const request = await this.buildGetRequest(`business-tools/members/${uid}`, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async updateMember(uid: string, updatedMember: any): Promise<UserProfile> {
    this.logger(`[BusinessToolsClient] update member detail for ${uid}`);
    const request = await this.buildPutRequest(`business-tools/members/${uid}`, updatedMember, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async sendGeneralNotification(
    userList: string[],
    notification: { title: string; message: string },
  ): Promise<boolean> {
    this.logger(`[BusinessToolsClient] sending general notification to ${userList}`);
    const payload = {
      userList,
      notification,
    };
    const request = await this.buildPostRequest(
      'business-tools/generalNotification',
      payload,
      true, /* local server */
    );
    const result = await this.fetch(
      request.url,
      this.buildFetchOptionsWithUserProvidedAuth(request.options),
    );
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getMemberComments(uid: string): Promise<IAccountComment[]> {
    this.logger(`[BusinessToolsClient] getting member comments for ${uid}`);
    const request = await this.buildGetRequest(`business-tools/members/${uid}/comments`, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data.comment;
  }

  public async updateLeadStatus(uid: string, leadStatus: LeadStatus): Promise<string> {
    this.logger(`[BusinessToolsClient] updating lead status ${uid}`);
    const request = await this.buildPutRequest(`business-tools/members/${uid}/leadStatus`, {
      leadStatus,
    }, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async updateTeam(uid: string, teamid: string): Promise<string> {
    this.logger(`[BusinessToolsClient] updating team for user ${uid}`);
    const request = await this.buildPutRequest(`business-tools/member/${uid}/team`, {
      teamid,
    }, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async updateComments(uid: string, comment: string): Promise<IAccountComment[]> {
    this.logger(`[BusinessToolsClient] update member comments for ${uid}`);
    const request = await this.buildPutRequest(`business-tools/members/${uid}/comments`, { comment }, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async saveFollowUpDate(uid: string, followUpDate: string): Promise<{ updatedFollowUpDate: Date | undefined }> {
    this.logger(`[BusinessToolsClient] update member follow up date for ${uid}`);
    const request = await this.buildPutRequest(`business-tools/members/${uid}/followUpDate`, { followUpDate }, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getFollowUpDate(uid: string): Promise<{ followUpDate: Date | undefined }> {
    this.logger(`[BusinessToolsClient] update member follow up date for ${uid}`);
    const request = await this.buildGetRequest(`business-tools/members/${uid}/followUpDate`, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async removeFollowUpDate(uid: string) {
    this.logger(`[BusinessToolsClient] update member follow up date for ${uid}`);
    const request = await this.buildDeleteRequest(`business-tools/members/${uid}/followUpDate`, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async createMemberReport(report: MembersReportData) {
    this.logger(`[BusinessToolsClient] create new member report: ${report.reportName}`);
    const request = await this.buildPostRequest('business-tools/memberReport', report, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getAllMemberReports() {
    this.logger('[BusinessToolsClient] getting all member reports');
    const request = await this.buildGetRequest('business-tools/memberReports', true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async updateMemberReport(reportId: string, report: MembersReportData) {
    this.logger(`[BusinessToolsClient] updating member report: ${report.reportName}`);
    const request = await this.buildPutRequest(`business-tools/memberReport/${reportId}`, report, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getDashboardConfig() {
    this.logger('[BusinessToolsClient] getting all member reports');
    const request = await this.buildGetRequest('business-tools/dashboardConfig', true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async updateReportOrderList(reportId: string, moveDown: boolean) {
    this.logger(`[BusinessToolsClient] updating member report: ${reportId}`);
    const request = await this.buildPutRequest('business-tools/dashboardConfig', { reportId, moveDown }, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async deleteMemberReport(reportId: string) {
    this.logger(`[BusinessToolsClient] deleting member report: ${reportId}`);
    const request = await this.buildDeleteRequest(`business-tools/memberReport/${reportId}`, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getAllWorkoutsForGym(
    gymName: string,
    maxPagesPerScope: number = 1,
    onProgress?: (loaded: number) => void,
  ): Promise<IUserWorkoutSessionMemberReport[]> {
    this.logger(`[BusinessToolsClient] getting all workouts for ${gymName} (maxPagesPerScope=${maxPagesPerScope})`);

    let loadedRows = 0;

    const fetchAllPagesForScope = async (scope: 'gym' | 'cross-gym') => {
      const rowsByUniqueReference = new Map<string, IUserWorkoutSessionMemberReport>();
      let lastPageToken: string | null = null;
      let pagesFetched = 0;
      do {
        const params = new URLSearchParams({ scope, limit: String(WORKOUT_PAGE_SIZE) });
        if (lastPageToken) {
          params.set('lastPageToken', lastPageToken);
        }
        const url = `business-tools/gyms/${gymName}/workouts?${params.toString()}`;
        const request = await this.buildGetRequest(url, true);
        const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
        const page: IPagedQueryResult<IUserWorkoutSessionMemberReport> = await this.assertHttp200AndGetJsonFromResult(
          result,
        );
        const previousRowsForScope = rowsByUniqueReference.size;
        for (const row of page.items) {
          rowsByUniqueReference.set(row.uniqueReference, row);
        }
        loadedRows += rowsByUniqueReference.size - previousRowsForScope;
        onProgress?.(loadedRows);
        lastPageToken = page.lastPageToken;
        pagesFetched += 1;
        if (pagesFetched >= maxPagesPerScope && lastPageToken !== null) {
          this.logger(
            `[BusinessToolsClient] capping workouts fetch for ${gymName} (${scope}) at ${maxPagesPerScope} pages `
            + `(${rowsByUniqueReference.size} rows); remaining pages skipped`,
          );
          break;
        }
      } while (lastPageToken !== null);
      return Array.from(rowsByUniqueReference.values());
    };

    const [gymRows, crossGymRows] = await Promise.all([
      fetchAllPagesForScope('gym'),
      fetchAllPagesForScope('cross-gym'),
    ]);
    return [...gymRows, ...crossGymRows];
  }

  public async uploadAvatar(uid: string, base64Image: string) {
    this.logger(`[BusinessToolsClient] updating avatar for user with id ${uid}`);
    const request = await this.buildPostRequest(`user/${uid}/avatar`, {
      base64Image,
    }, true);
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getMonthlyPulseSupport(
    gymName: string,
    period: IMonthlyPulsePeriod,
    runOptions?: IPulseRunOptions,
  ): Promise<any> {
    const params = new URLSearchParams();
    if (period.month) {
      params.set('month', period.month);
    } else if (period.from && period.to) {
      params.set('from', period.from.toISOString());
      params.set('to', period.to.toISOString());
    } else {
      throw new Error('Invalid monthly pulse period; expected month or from and to');
    }
    if (runOptions) {
      params.set('includeTestUsers', String(runOptions.includeTestUsers));
      params.set('includeUnonboarded', String(runOptions.includeUnonboarded));
    }
    const request = await this.buildGetRequest(
      `business-tools/gyms/${gymName}/monthly-pulse?${params.toString()}`,
      true,
    );
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async createSpecialOffer(
    uid: string,
    specialOffer: ICreateSpecialOfferParams,
  ): Promise<ISpecialOfferDisplay> {
    this.logger(`[BusinessToolsClient] creating special offer for: ${uid}`);
    const request = await this.buildPostRequest(
      `business-tools/members/${uid}/special-offers`,
      specialOffer,
      true,
    );
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async updateSpecialOffer(
    uid: string,
    specialOfferId: string,
    specialOffer: ICreateSpecialOfferParams,
  ): Promise<ISpecialOfferDisplay> {
    this.logger(`[BusinessToolsClient] updating special offer for: ${uid}`);
    const request = await this.buildPutRequest(
      `business-tools/members/${uid}/special-offers/${specialOfferId}`,
      specialOffer,
      true,
    );
    const result = await this.fetch(request.url, this.buildFetchOptionsWithUserProvidedAuth(request.options));
    return this.assertHttp200AndGetJsonFromResult(result);
  }
}
